<template>
  <div>
    <div role="search" class="relative flex flex-col w-full gap-2 search-input rounded-3xl">
      <label class="text-lg font-futuraEfDemiBold text-newColors-gray-content" for="query">{{ this.translations.heading }} </label>
      <input
        id="query"
        ref="topSearchField"
        v-model="inputText"
        type="text"
        @focus="handleFocus"
        class="w-full py-1 pl-4 pr-2 text-lg bg-white bg-no-repeat border-2 rounded-lg outline-none text-newColors-gray-content border-newColors-gray-contentD font-futuraEfBook bg-icon-search-with-border bg-line-height bg-center-right"
        :placeholder="translations.placeholder"
        name="query"
        autocomplete="off"
        @keyup="keyUpEvents($event)"
        @keyup.esc="handleEsc" />
      <ul v-if="isSearchFieldOpen" v-click-outside="closeAutoComplete" class="absolute left-0 z-20 bg-white md:border-2 md:border-t-0 md:border-solid md:border-gray-standard top-[84px] md:top-[68px] mb-4" :style="inputWidth()">
        <li v-for="(item, index) in matches" :key="index" :class="{ active: hover }" class="relative flex items-center w-full cursor-pointer autocompleteRemoveFocus group hover:bg-gray-ultralight" @mouseover="mouseOver(index)">
          <a tabindex="0" href="#" class="block w-full px-4 py-2.5 !text-black md:text-lg focus:bg-gray-ultralight text-base-dropdown" @keyup.enter="autocompleteClick(item)" @keyup.esc="handleEsc" @click="autocompleteClick(item, $event)">
            {{ getMatchLabel(item) }}
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { clickOutside } from '../../../directives/clickOutside'
import cityList from '../../../helpers/cities.js'
import countyList from '../../../helpers/counties.js'

export default {
  name: 'EllevioWebSearchInput',
  directives: {
    'click-outside': clickOutside
  },
  props: {
    translations: {
      type: Object,
      default: () => {}
    },
    value: {
      type: String,
      default: ''
    },
    numberOfItems: {
      type: Number,
      default: 5
    },
    showAutocompleteList: {
      type: Boolean,
      default: true
    },
    emptySearchFieldForMobile: {
      type: Boolean,
      default: false
    },
    closeFullscreen: {
      type: Boolean,
      default: false
    },
    showAllCounties: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showAutoComplete: false,
      hover: [],
      inputText: this.value,
      timer: null,
      getAutocompleteItems: [],
      isFocused: false,
      searchFullscreenMobile: false
    }
  },
  computed: {
    baseUrl() {
      let env = {}
      try {
        env = import.meta.env
        if (typeof env !== 'object') env = {}
      } catch (_) {
        env = {}
      }
      return env.VUE_APP_BASE_URL ?? ''
    },
    matches() {
      if (this.inputText == '') {
        return []
      }
      return this.getAutocompleteItems

      /*
      let env = {}
      try {
        env = import.meta.env
        if (typeof env !== 'object') env = {}
      } catch (_) {
        env = {}
      }
      */

      /*
      if (env.NODE_ENV === 'development') {
        return this.getAutocompleteItems.filter((item) => item.title.toLowerCase().includes(this.inputText.toLowerCase())).slice(0, this.numberOfItems)
      } else {
        return this.getAutocompleteItems.slice(0, this.numberOfItems)
      }
      */
    },
    isSearchFieldOpen() {
      return this.showAutoComplete === true && this.matches && this.matches.length > 0 && this.showAutocompleteList
    }
  },
  watch: {
    emptySearchFieldForMobile: function (newVal) {
      if (newVal) {
        this.inputText = ''
      }
    },
    isSearchFieldOpen: function (newVal) {
      this.$emit('searchfield-open', newVal)
    }
  },
  async mounted() {
    this.$nextTick(async function () {
      this.inputWidth()
    })
  },
  methods: {
    handleFocus() {
      this.isFocused = true
      this.searchFullscreenMobile = true
      this.$emit('is-focused', this.isFocused)
      this.$emit('search-mobile-fullscreen', this.searchFullscreenMobile)
    },
    inputWidth() {
      return `width:${this.$refs.topSearchField && this.$refs.topSearchField.clientWidth + 4}px;`
    },
    mouseOver(index) {
      this.hover[index] = true
    },
    closeAutoComplete() {
      this.showAutoComplete = false
    },
    getData: async function (inputText) {
      const counties = this.showAllCounties ? countyList.filter((item) => item.County.toLowerCase().includes(inputText.toLowerCase())).slice(0, this.numberOfItems) : []
      const cities = cityList
        .filter((item) => item.Locality.toLowerCase().includes(inputText.toLowerCase()))
        .sort(this.getCitySortFunction(inputText))
        .slice(0, this.numberOfItems)
      return [...counties, ...cities].slice(0, this.numberOfItems)
    },
    getCitySortFunction(inputText) {
      return (a, b) => {
        // City (Locality) that starts with search string are placed before city that only contains search string
        if (a.Locality.toLowerCase().startsWith(inputText) && !b.Locality.toLowerCase().startsWith(inputText)) {
          return -1
        }
        if (!a.Locality.toLowerCase().startsWith(inputText) && b.Locality.toLowerCase().startsWith(inputText)) {
          return 1
        }

        // City with the same name as the citys municipality are placed before city with different name from its municipality
        if (a.Locality.toLowerCase() === a.Municipality.toLowerCase() && b.Locality.toLowerCase() !== b.Municipality.toLowerCase()) {
          return -1
        }
        if (a.Locality.toLowerCase() !== a.Municipality.toLowerCase() && b.Locality.toLowerCase() === b.Municipality.toLowerCase()) {
          return 1
        }

        // If both compared city names match their municipality then check if county name match
        if (a.Locality.toLowerCase() === a.Municipality.toLowerCase() && b.Locality.toLowerCase() === b.Municipality.toLowerCase()) {
          if (a.Municipality.toLowerCase() === a.County.toLowerCase() && b.Municipality.toLowerCase() !== b.County.toLowerCase()) {
            return -1
          }
          if (a.Municipality.toLowerCase() !== a.County.toLowerCase() && b.Municipality.toLowerCase() === b.County.toLowerCase()) {
            return 1
          }
        }

        // Sort matches in alphabetical order
        if (a.Locality.toLowerCase() < b.Locality.toLowerCase()) {
          return -1
        }
        if (a.Locality.toLowerCase() > b.Locality.toLowerCase()) {
          return 1
        }

        return 0
      }
    },
    getMatchLabel(item) {
      // Label for city match
      if (item.Locality) {
        return item.Locality === item.County ? item.Locality : `${item.Locality}, ${item.County}`
      }

      // Label for County match
      return item.County ? item.County : ''
    },
    handleEsc() {
      this.showAutoComplete = false
      this.inputText = ''
    },
    keyUpEvents: async function ($event) {
      if (!this.showAutocompleteList) return
      clearTimeout(this.timer)
      this.inputText = $event.target.value
      if (this.inputText.length > 2) {
        this.timer = setTimeout(
          async function () {
            this.getAutocompleteItems = await this.getData(this.inputText)
          }.bind(this),
          150
        )
        this.showAutoComplete = true
      } else {
        this.showAutoComplete = false
      }
    },
    autocompleteClick: function (item, event) {
      event.preventDefault()
      this.searchFullscreenMobile = false
      this.$emit('search-mobile-fullscreen', this.searchFullscreenMobile)
      this.$emit('autocomplete-click', { lat: item.Latitude, lon: item.Longitude, isCity: item.Locality ? true : false })
      this.inputText = ''
      this.showAutoComplete = false
    }
  }
}
</script>
<style scoped>
.search-input {
  z-index: 900;
}
.search-input input::placeholder {
  font-style: italic;
}
@media screen and (min-width: 768px) {
  .search-input {
    z-index: 1300;
  }
}
.bg-icon-search-with-border {
  background-image: url('/src/assets/images/search-with-border.svg');
  background-size: 30px;
}
</style>
